import React, { useEffect, useState } from 'react';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

import {
  PageContent,
  PageSection,
  PageHeader,
  Subtitle,
  ControlContainerRow,
  ControlContainer,
  StyledCard,
} from 'common/styles/page';
import Select from 'react-select';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

import OverviewLineChart from './OutcomesOverviewLineChart';
import ToggleControl from '../../../common/components/ToggleChartControl';

import {
  useApiPortalV1AssessmentsResultsListQuery,
  useListAssessmentPeriodMetricResultsQuery,
} from 'areas/assessment-schedule/api';
import { useSelector } from 'react-redux';
import OutcomesRetentionPredictions from './OutcomesRetentionPredictions';
import { SemanticICONS } from 'semantic-ui-react';
import {
  getCurrentOrganizationId,
  getMostRecentAssessmentPeriod,
  getSelectedAssessmentPeriod,
} from 'common/selectors/auth';
import LoadingSpinner from 'common/components/LoadingSpinner';
import OrganizationRecordsDataTable from './OutcomesOrganizationRecordsTable';
import { organizationRecordApi } from '../api/organizationRecordApi';
import { organizationRecordValueApi } from 'areas/settings/organization/api/organizationRecordValueApi';
import { StyledIconButton } from 'common/components/button/StyledButton';

import { AggregateType } from 'common/constants/AggregateType';
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const OutcomesOrganization = () => {
  type SelectOption = { value: string; label: string };
  const [filterRecords, setFilterRecords] = useState<SelectOption[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);
  const [selectedRecordName, setSelectedRecordName] = useState<string | null>(null);

  const [filterOutcomes, setFilterOutcomes] = useState<SelectOption[]>([]);
  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null); // outcome_name
  const [selectedOutcomeName, setSelectedOutcomeName] = useState<string | null>(null);

  const [hasOrgRecords, setHasOrgRecords] = useState<boolean>(false);
  const organizationId = useSelector(getCurrentOrganizationId);
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod) as any;
  const mostRecentAssessmentPeriod = useSelector(getMostRecentAssessmentPeriod) as any;
  const [showDataTable, setShowDataTable] = useState<boolean>(false);

  const [maxYAxis, setMaxYAxis] = useState<number>(10);
  const [chartType, setChartType] = useState('bar');
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  const { data: outcomes_data, isLoading: isLoadingOutcomes } =
    useApiPortalV1AssessmentsResultsListQuery({ aggregateType: AggregateType.OUTCOME_SCORES });

  const { data: overallScores, isLoading: isLoadingOverall } =
    useListAssessmentPeriodMetricResultsQuery();

  const { data: organization_records_raw, isLoading: isLoadingOrgRecords } =
    organizationRecordApi.endpoints.apiAdminV1OrganizationRecordsList.useQuery({ organizationId });

  const { data: organization_record_values } =
    organizationRecordValueApi.endpoints.apiAdminV1OrganizationRecordValuesList.useQuery({ organizationId });

  const [orgOutcomeLineChartData, setOrgOutcomeLineChartData] = useState<Array<any>>([]);
  const [coreOutcomeLineChartData, setCoreOutcomeLineChartData] = useState<Array<any>>([]);

  const currentOutcomeStructure =
    selectedAssessmentPeriod?.outcomes ||
    mostRecentAssessmentPeriod?.outcomes ||
    [];

  useEffect(() => {
    const normalizedFromStructure = Array.isArray(currentOutcomeStructure)
      ? currentOutcomeStructure
          .map((outcome: any) => ({
            value: outcome?.id?.toString?.() || '',
            label: outcome?.name || '',
          }))
          .filter((outcome: SelectOption) => outcome.value && outcome.label)
      : [];

    const outcomes = Array.from(
      new Map(normalizedFromStructure.map(outcome => [outcome.value, outcome])).values()
    ).sort((a, b) => a.label.localeCompare(b.label));

    if (outcomes.length > 0) {
      setFilterOutcomes(outcomes);

      const isSelectedValid = outcomes.some(o => o.value === selectedOutcome);
      if (!isSelectedValid) {
        setSelectedOutcome(outcomes[0].value);
        setSelectedOutcomeName(outcomes[0].label);
      }
    } else {
      setFilterOutcomes([]);
      setSelectedOutcome(null);
      setSelectedOutcomeName(null);
    }
  }, [currentOutcomeStructure, selectedOutcome]);

  useEffect(() => {
    if (!selectedOutcome && filterOutcomes.length > 0) {
      setSelectedOutcome(filterOutcomes[0].value);
      setSelectedOutcomeName(filterOutcomes[0].label);
    }
  }, [filterOutcomes, selectedOutcome]);

  useEffect(() => {
    let records: SelectOption[] = [];
    if (organization_record_values && organization_record_values.length > 0) {
      records = organization_record_values
        .map((recordValue: any) => ({
          value: recordValue.id.toString(),
          label: recordValue.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    const updateRecordSelect = [...records];
    setHasOrgRecords(updateRecordSelect.length > 0 ? true : false);

    setFilterRecords(updateRecordSelect);
    setSelectedRecord(updateRecordSelect[0]?.value);
    setSelectedRecordName(updateRecordSelect[0]?.label);
  }, [organization_record_values, organizationId]);

  useEffect(() => {
    if (!hasOrgRecords && chartType === 'line') {
      setChartType('bar');
    }
  }, [hasOrgRecords]);

  // Load core outcomes data for charts
  useEffect(() => {
    if (outcomes_data && outcomes_data.length > 0 && overallScores && overallScores.length > 0 && selectedOutcome) {
      const accumulatedData: any[] = [];
      let dataSetCounter = 0;

      const sortedOverallScoreData = [...overallScores].sort((a, b) =>
        (a.assessment_period_start || '').localeCompare(b.assessment_period_start || '')
      );

      const sortedOutcomeData = [...outcomes_data].sort((a, b) =>
        a.result.assessment_period_start.localeCompare(b.result.assessment_period_start)
      );

      sortedOverallScoreData.forEach((scoreItem, index) => {
        const outcomeAP = sortedOutcomeData[index];

        const matchedOutcome = outcomeAP?.result?.outcomes?.find(
          (outcome: { outcome_name: string | null; outcome_id?: string | number | null }) =>
            outcome.outcome_id?.toString?.() === selectedOutcome ||
            outcome.outcome_name === selectedOutcome ||
            outcome.outcome_name === selectedOutcomeName
        );
        const outcome_score = matchedOutcome?.overall_score;

        if (outcomeAP) {
          const overviewData: any[] = [
            {
              overall_score: scoreItem?.overall_score,
              option_name: 'Overall Score',
              option_label: 'Culture Score',
            },
            {
              overall_score: outcome_score,
              option_name: 'Outcome Score',
              option_label: selectedOutcomeName || matchedOutcome?.outcome_name,
            },
          ];

          accumulatedData.push({
            data_set: dataSetCounter++,
            data: overviewData,
            name: scoreItem?.assessment_period_name,
            start_date: scoreItem?.assessment_period_start,
            end_date: scoreItem?.assessment_period_end,
          });
        }
      });
      setCoreOutcomeLineChartData(accumulatedData);
    } else {
      setCoreOutcomeLineChartData([]);
    }
    setMaxYAxis(10);
  }, [overallScores, outcomes_data, selectedOutcome, selectedOutcomeName]);


  useEffect(() => {
    setPagePrompts([
      {
        title: 'Organizational Outcomes Summary',
        displayText: `Summarize the organizational outcomes for ${selectedOutcomeName || 'all outcome'} data.`,
        prompt:
          `Review the Organizational Outcomes page for ${selectedOutcomeName || 'the selected outcome'} and explain how the outcome metrics relate to culture scores. Highlight the key signals for leaders, the role of current outcome trends, and whether any compensation or record-based measures are aligned with culture metrics.`,
      },
      {
        title: 'Outcome Trend Drivers',
        displayText: `Identify the strongest trends for ${selectedOutcomeName || 'all outcome'} data.`,
        prompt:
          `Based on the selected outcome ${selectedOutcomeName || 'filter'} and organizational records, identify the strongest trends and the drivers behind them. Explain which outcomes are improving or declining, and what those signals suggest for the organization.`,
      },
      {
        title: 'Leader Actions from Outcomes',
        displayText: `Recommend cultural improvement actions based on ${selectedOutcomeName || 'all outcome'} data.`,
        prompt:
          `Recommend practical actions leaders should take based on the selected outcome ${selectedOutcomeName || 'filter'} and the organizational outcomes and culture score comparisons shown on this page. Focus on actions that close gaps, reinforce positive signals, and improve outcome alignment with culture.`,
      },
    ]);

    setPageInfoTitle('About Organizational Outcomes');
    setPageInfoIntro(
      'This page helps leaders understand how outcome metrics compare with culture scores and organizational records. It shows whether outcomes are improving, where the strongest signals lie, and what context should guide next steps.',
    );
    setPageInfoItems([
      {
        term: 'Outcome alignment',
        description: 'See how outcome metrics align with your culture measures and organizational signals.',
      },
      {
        term: 'Trend interpretation',
        description: 'Understand whether trends are positive, negative, or mixed and why they matter.',
      },
      {
        term: 'Recommended follow-up',
        description: 'Turn outcome signals into leader-focused next steps and priorities.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro, selectedOutcomeName]);

  useEffect(() => {
    if (chartType !== 'line') {
      setMaxYAxis(10);
      setOrgOutcomeLineChartData([]);
      return;
    }

    if (
      organization_records_raw &&
      organization_records_raw.length > 0 &&
      overallScores &&
      overallScores.length > 0 &&
      selectedRecord
    ) {
      // Filter records for selected record value
      const filteredRecords = organization_records_raw.filter((record: any) => {
        const recordValueId = typeof record.organization_record_value === 'object'
          ? record.organization_record_value?.id
          : record.organization_record_value;
        return recordValueId?.toString() === selectedRecord;
      });

      // Create map of assessment period ID to record value
      const recordsByPeriod = new Map();
      filteredRecords.forEach((record: any) => {
        const periodId = typeof record.assessment_period === 'object'
          ? record.assessment_period.id
          : record.assessment_period;
        if (periodId) {
          recordsByPeriod.set(periodId, parseFloat(record.value) || 0);
        }
      });

      // Get max range from organization record values
      const selectedRecordValue = organization_record_values?.find(
        (rv: any) => rv.id.toString() === selectedRecord
      );
      setMaxYAxis(selectedRecordValue?.max_range || 100);

      const accumulatedData: any[] = [];
      let dataSetCounter = 0;

      const sortedOverallScoreData = [...overallScores].sort((a, b) =>
        (a.assessment_period_start || '').localeCompare(b.assessment_period_start || '')
      );

      sortedOverallScoreData.forEach((overallScoreItem) => {
        const periodId = overallScoreItem?.assessment_period_id;
        const recordValue = recordsByPeriod.get(periodId) || 0;

        const overviewData = [
          {
            overall_score: overallScoreItem?.overall_score,
            option_name: 'Overall Score',
            option_label: 'Culture Score',
          },
          {
            overall_score: recordValue,
            option_name: 'Outcome Score',
            option_label: selectedRecordName,
          },
        ];

        accumulatedData.push({
          data_set: dataSetCounter++,
          data: overviewData,
          name: overallScoreItem?.assessment_period_name,
          start_date: overallScoreItem?.assessment_period_start,
          end_date: overallScoreItem?.assessment_period_end,
        });
      });
      setOrgOutcomeLineChartData(accumulatedData);
    } else {
      setOrgOutcomeLineChartData([]);
    }
  }, [overallScores, organization_records_raw, organization_record_values, selectedRecord, selectedRecordName, chartType]);

  const outcomeLabel = selectedOutcomeName ?? filterOutcomes[0]?.label ?? '';
  const recordLabel = selectedRecordName ?? filterRecords[0]?.label ?? '';
  const titleForBar = `Culture Scores${outcomeLabel ? ` and ${outcomeLabel}` : ''}`;
  const titleForLine = `Culture Scores${recordLabel ? ` and ${recordLabel}` : ''}`;
  const hasOutcomes = filterOutcomes.length > 0;
  const shouldShowNoOutcomesMessage = chartType === 'bar' && !hasOutcomes;

  const isInitialLoading =
    isLoadingOutcomes ||
    isLoadingOverall ||
    (chartType === 'line' && isLoadingOrgRecords);

  if (isInitialLoading) {
    return (
      <PageContent>
        <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 360 }}>
          <LoadingSpinner />
        </PageSection>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <PageSection padbottom="10px">
        <PageHeader
          title="Organizational Outcomes"
          caption="Outcomes compared to cultural pillar scores"
          controls={
            <ControlContainerRow>
              <ControlContainer justify="left">
                <ToggleControl
                  buttonLabels={[
                    { type: 'bar', icon: 'chart bar' },
                    ...(hasOrgRecords
                      ? [{ type: 'line', icon: 'line graph' as SemanticICONS }]
                      : []),
                    { type: 'predict', icon: 'lightbulb' },
                  ]}
                  setChartType={setChartType}
                  activeToggle={chartType}
                />
              </ControlContainer>
            </ControlContainerRow>
          }
        />
      </PageSection>

      {chartType === 'bar' || chartType === 'line' ? (
        <>
          <PageSection display="flex" gap="10px" alignItems="flex-start">
            <StyledCard>
              <PageSection padtop padright="15px" padleft padbottom>
                <PageSection padbottom display="flex" justifyContent="space-between">
                  {chartType === 'bar' ? (
                    <>
                      <Subtitle>{titleForBar}</Subtitle>
                      {!shouldShowNoOutcomesMessage && (
                        <Select
                          options={filterOutcomes}
                          placeholder="Select Outcome"
                          value={filterOutcomes.find(option => option.value === selectedOutcome)}
                          onChange={option => {
                            setSelectedOutcome(option ? option.value : null);
                            setSelectedOutcomeName(option ? option.label : null);
                            setMaxYAxis(10);
                          }}
                          styles={{
                            container: base => ({
                              ...base,
                              width: '250px',
                            }),
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Subtitle>{titleForLine}</Subtitle>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <Select
                          options={filterRecords}
                          placeholder="Select Record"
                          value={filterRecords.find(option => option.value === selectedRecord)}
                          onChange={option => {
                            setSelectedRecord(option ? option.value : null);
                            setSelectedRecordName(option ? option.label : null);
                          }}
                          styles={{
                            container: base => ({
                              ...base,
                              width: '250px',
                            }),
                          }}
                        />
                        <StyledIconButton
                          icon={showDataTable ? 'chart line' : 'edit outline'}
                          title={showDataTable ? 'Show Chart' : 'Edit Data'}
                          onClick={() => setShowDataTable(!showDataTable)}
                          disabled={false}
                        />
                      </div>
                    </>
                  )}
                </PageSection>
                {shouldShowNoOutcomesMessage ? (
                  <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 300, width: '100%' }}>
                    <div>No outcomes data is available for the selected assessment period.</div>
                  </PageSection>
                ) : chartType === 'line' && showDataTable && selectedRecord ? (
                  <OrganizationRecordsDataTable
                    organizationId={organizationId}
                    recordValueId={parseInt(selectedRecord)}
                    recordValueName={selectedRecordName || ''}
                  />
                ) : (
                  <PageSection display="flex" justifyContent="center" style={{ height: '490px' }}>
                    <OverviewLineChart
                      lineChartData={
                        chartType === 'bar' ? coreOutcomeLineChartData : orgOutcomeLineChartData
                      }
                      maxYAxis={maxYAxis}
                    />
                  </PageSection>
                )}
              </PageSection>
            </StyledCard>
          </PageSection>
        </>
      ) : (
        <OutcomesRetentionPredictions />
      )}
    </PageContent>
  );
};
export default OutcomesOrganization;

